// The Activities & Issues page's feed: the site's COMMITTED activities
// (docs/specs/2026-09-17-task-commit-trusstor-activity-design.md).
//
// A row is an overlay record joined with the plan task it belongs to and with
// Trusstor's live group/area status. There is no cache behind it: when Trusstor
// is unreachable the backend answers 502 and this throws `UpstreamError` —
// the page says so rather than showing a status it cannot vouch for
// (invariant 7).

import type { ActivityGroupStatus, ISchedulerTask } from '../interfaces/IScheduler.js';
import { parseStandardErrors } from './monitor.js';
import type { AuthedFetch } from './transport.js';

/**
 * A frame of the site's `/api/sites/:siteId/activities/stream`. Every frame is
 * an INVALIDATION, not a payload — "something about this site's activities
 * changed, refetch" — so a client never merges a frame into a joined row and a
 * shape change upstream cannot corrupt what it shows. Web and mobile read one
 * definition.
 */
export type ActivitiesStreamEvent =
  | { channel: 'trusstor'; data: Record<string, never> }
  | { channel: 'overlay'; data: { scheduleNumber: string; taskId: string; kind: string } }
  | { channel: '_resync'; data: Record<string, never> };

export interface ICommittedActivity {
  scheduleNumber: string;
  /** The composed task — plan fields, overlay fields, per-area status. */
  task: ISchedulerTask;
  /** The Trusstor group's own status; `null` for an administrative activity. */
  activityStatus: ActivityGroupStatus | null;
  committedAt: string;
  committedBy: string;
  /** Trusstor reports every area complete but nobody has marked the task done.
   *  A suggestion the UI may offer — never an automatic transition. */
  suggestDone: boolean;
}

/** One pickable commit assignee. `username` is what a commit sends upstream. */
export interface ICommitAssignee {
  username: string;
  name?: string;
  /** The caller themself — the review's default assignee. */
  isYou?: boolean;
}

export function createActivitiesApi(authedFetch: AuthedFetch) {
  async function fetchCommittedActivities(
    siteId: string,
    signal?: AbortSignal,
  ): Promise<ICommittedActivity[]> {
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/activities`, { signal });
    if (res.ok) return ((await res.json()) as { activities: ICommittedActivity[] }).activities;
    return parseStandardErrors(res);
  }

  async function fetchCommitAssignees(
    siteId: string,
    signal?: AbortSignal,
  ): Promise<ICommitAssignee[]> {
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/activities/assignees`, { signal });
    if (res.ok) return ((await res.json()) as { assignees: ICommitAssignee[] }).assignees;
    return parseStandardErrors(res);
  }

  return { fetchCommittedActivities, fetchCommitAssignees };
}
